/**
 * Platform Values Settings - GLOBAL ON TAP
 * 
 * Super Admin configuration page for platform-wide values that affect:
 * - ROI calculations
 * - Revenue estimates
 * - Health thresholds
 * - Conversion metrics
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { platformAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CommandBar from '../../components/CommandBar';
import { toast } from 'sonner';
import {
  Settings,
  DollarSign,
  Target,
  Activity,
  Save,
  RotateCcw,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

// Value categories for organization
const VALUE_CATEGORIES = {
  revenue: {
    title: { en: 'Revenue Settings', fr: 'Paramètres de Revenus' },
    description: { en: 'Configure revenue calculation values', fr: 'Configurer les valeurs de calcul des revenus' },
    icon: DollarSign,
    color: '#22c55e',
    keys: ['avg_revenue_per_tap', 'avg_revenue_per_review', 'avg_revenue_per_survey', 'avg_customer_value', 'currency', 'currency_symbol']
  },
  costs: {
    title: { en: 'Costs & ROI', fr: 'Coûts & ROI' },
    description: { en: 'Cost of acquisition and ROI calculation settings', fr: 'Coût d\'acquisition et paramètres de calcul ROI' },
    icon: Target,
    color: '#ef4444',
    keys: ['cost_of_acquisition', 'cost_per_conversion', 'cost_per_tap_card', 'monthly_platform_fee', 'roi_calculation_period_days', 'target_roi_percentage']
  },
  targets: {
    title: { en: 'Target Metrics', fr: 'Métriques Cibles' },
    description: { en: 'Target conversion rates and satisfaction scores', fr: 'Taux de conversion cibles et scores de satisfaction' },
    icon: Target,
    color: '#3b82f6',
    keys: ['target_conversion_rate', 'target_satisfaction_score']
  },
  health: {
    title: { en: 'Health Thresholds', fr: 'Seuils de Santé' },
    description: { en: 'Location health score thresholds', fr: 'Seuils des scores de santé des locations' },
    icon: Activity,
    color: '#f59e0b',
    keys: ['health_threshold_good', 'health_threshold_warning']
  },
  weights: {
    title: { en: 'Review Weights', fr: 'Poids des Avis' },
    description: { en: 'Weighting for different review platforms', fr: 'Pondération des différentes plateformes d\'avis' },
    icon: Activity,
    color: '#8b5cf6',
    keys: ['review_weight_google', 'review_weight_tripadvisor', 'review_weight_yelp']
  }
};

// Human-readable labels for each key
const VALUE_LABELS = {
  avg_revenue_per_tap: { en: 'Avg Revenue per Tap ($)', fr: 'Revenu Moy. par Tap ($)' },
  avg_revenue_per_review: { en: 'Avg Revenue per Review ($)', fr: 'Revenu Moy. par Avis ($)' },
  avg_revenue_per_survey: { en: 'Avg Revenue per Survey ($)', fr: 'Revenu Moy. par Sondage ($)' },
  avg_customer_value: { en: 'Avg Customer Value ($)', fr: 'Valeur Client Moyenne ($)' },
  cost_of_acquisition: { en: 'Cost of Acquisition ($)', fr: 'Coût d\'Acquisition ($)' },
  cost_per_conversion: { en: 'Cost per Conversion ($)', fr: 'Coût par Conversion ($)' },
  cost_per_tap_card: { en: 'Cost per Tap Card ($)', fr: 'Coût par Carte Tap ($)' },
  monthly_platform_fee: { en: 'Monthly Platform Fee ($)', fr: 'Frais Mensuels Plateforme ($)' },
  target_conversion_rate: { en: 'Target Conversion Rate (%)', fr: 'Taux de Conversion Cible (%)' },
  target_satisfaction_score: { en: 'Target Satisfaction (1-5)', fr: 'Satisfaction Cible (1-5)' },
  target_roi_percentage: { en: 'Target ROI (%)', fr: 'ROI Cible (%)' },
  roi_calculation_period_days: { en: 'ROI Period (days)', fr: 'Période ROI (jours)' },
  health_threshold_good: { en: 'Good Health Threshold (%)', fr: 'Seuil Santé Bon (%)' },
  health_threshold_warning: { en: 'Warning Threshold (%)', fr: 'Seuil Avertissement (%)' },
  review_weight_google: { en: 'Google Weight (0-1)', fr: 'Poids Google (0-1)' },
  review_weight_tripadvisor: { en: 'TripAdvisor Weight (0-1)', fr: 'Poids TripAdvisor (0-1)' },
  review_weight_yelp: { en: 'Yelp Weight (0-1)', fr: 'Poids Yelp (0-1)' },
  currency: { en: 'Currency Code', fr: 'Code Devise' },
  currency_symbol: { en: 'Currency Symbol', fr: 'Symbole Devise' }
};

export default function PlatformValuesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Super Admin access required');
      navigate('/hotels');
      return;
    }
    fetchValues();
  }, [user, navigate]);

  useEffect(() => {
    // Check for changes
    const changed = JSON.stringify(values) !== JSON.stringify(originalValues);
    setHasChanges(changed);
  }, [values, originalValues]);

  const fetchValues = async () => {
    setLoading(true);
    try {
      const response = await platformAPI.getValues();
      setValues(response.data.values);
      setOriginalValues(response.data.values);
      setLastUpdated(response.data.last_updated);
      setUpdatedBy(response.data.updated_by);
    } catch (error) {
      toast.error('Failed to load platform values');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key, value) => {
    // Parse numeric values - remove leading zeros
    const originalType = typeof originalValues[key];
    let parsedValue = value;
    
    if (originalType === 'number') {
      // Clean leading zeros except for decimals like "0.5"
      if (typeof value === 'string') {
        value = value.replace(/^0+(?=\d)/, '');
      }
      parsedValue = value === '' ? 0 : parseFloat(value);
      if (isNaN(parsedValue)) parsedValue = 0;
    }
    
    setValues(prev => ({ ...prev, [key]: parsedValue }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send changed values
      const changedValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== originalValues[key]) {
          changedValues[key] = values[key];
        }
      });
      
      await platformAPI.updateValues(changedValues);
      setOriginalValues(values);
      setHasChanges(false);
      toast.success(i18n.language === 'fr' ? 'Valeurs sauvegardées' : 'Values saved successfully');
      fetchValues(); // Refresh to get updated timestamp
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save values');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(i18n.language === 'fr' 
      ? 'Voulez-vous vraiment réinitialiser toutes les valeurs par défaut?' 
      : 'Are you sure you want to reset all values to defaults?')) {
      return;
    }
    
    setSaving(true);
    try {
      await platformAPI.resetToDefaults();
      toast.success(i18n.language === 'fr' ? 'Valeurs réinitialisées' : 'Values reset to defaults');
      fetchValues();
    } catch (error) {
      toast.error('Failed to reset values');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValues(originalValues);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CommandBar
        title={i18n.language === 'fr' ? 'Valeurs Plateforme' : 'Platform Values'}
        subtitle={i18n.language === 'fr' ? 'Configuration des paramètres globaux' : 'Global settings configuration'}
        actions={
          <div className="flex items-center space-x-2">
            {hasChanges && (
              <span className="text-amber-500 text-sm flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Modifications non sauvegardées' : 'Unsaved changes'}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={!hasChanges || saving}
            >
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
              className="text-red-500 border-red-500 hover:bg-red-500/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {i18n.language === 'fr' ? 'Défaut' : 'Reset'}
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </div>
        }
      />

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {i18n.language === 'fr' ? 'Retour au Centre de Contrôle' : 'Back to Control Center'}
        </button>

        {/* Last updated info */}
        {lastUpdated && (
          <div className="flex items-center text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
            {i18n.language === 'fr' ? 'Dernière mise à jour:' : 'Last updated:'}{' '}
            {new Date(lastUpdated).toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA')}
            {updatedBy && ` by ${updatedBy}`}
          </div>
        )}

        {/* Settings Tabs */}
        <Tabs defaultValue="revenue" className="space-y-6">
          <TabsList className="bg-card border border-border p-1">
            {Object.entries(VALUE_CATEGORIES).map(([key, cat]) => {
              const Icon = cat.icon;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="data-[state=active]:text-white"
                  style={{ '--active-color': cat.color }}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {cat.title[i18n.language] || cat.title.en}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(VALUE_CATEGORIES).map(([catKey, cat]) => {
            const Icon = cat.icon;
            return (
              <TabsContent key={catKey} value={catKey}>
                <Card className="bg-card border-border">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${cat.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div>
                        <CardTitle className="text-foreground">
                          {cat.title[i18n.language] || cat.title.en}
                        </CardTitle>
                        <CardDescription>
                          {cat.description[i18n.language] || cat.description.en}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {cat.keys.map(key => {
                        const label = VALUE_LABELS[key];
                        const isNumeric = typeof values[key] === 'number';
                        const hasChanged = values[key] !== originalValues[key];
                        
                        return (
                          <div key={key} className="space-y-2">
                            <Label 
                              htmlFor={key}
                              className={`flex items-center ${hasChanged ? 'text-amber-500' : 'text-foreground'}`}
                            >
                              {label?.[i18n.language] || label?.en || key}
                              {hasChanged && <span className="ml-2 text-xs">(modified)</span>}
                            </Label>
                            <Input
                              id={key}
                              type="text"
                              inputMode={isNumeric ? 'decimal' : 'text'}
                              value={values[key] ?? ''}
                              onChange={(e) => handleValueChange(key, e.target.value)}
                              className={`${hasChanged ? 'border-amber-500' : ''}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </main>
    </div>
  );
}
